import type { Feature, FeatureCollection, Geometry } from "geojson";

/** Géométries des graphiques tactiques côté client, calquées sur le serveur (generex_engine/graphics.py) et donc
 *  sur l'iPad : cercle de 64 sommets, zone R de 48 sommets, secteur sous le vent de 30° en 24 arcs, rayon
 *  terrestre de 6 378 137 m. Elles servent aux aperçus (tracé en cours, glissement) ; le dessin enregistré vient
 *  du serveur. */

export const EARTH_RADIUS_M = 6_378_137;
const HAZARD_ARC_DEG = 30;

export type LonLat = [number, number];

/** Point à `distance` mètres au cap `bearing` (nord, sens horaire), en équirectangulaire comme l'iPad. */
export function offset([lon, lat]: LonLat, distance: number, bearing: number): LonLat {
  const theta = (bearing * Math.PI) / 180;
  const dLat = (distance * Math.cos(theta)) / EARTH_RADIUS_M;
  const dLon = (distance * Math.sin(theta)) / (EARTH_RADIUS_M * Math.cos((lat * Math.PI) / 180));
  return [lon + (dLon * 180) / Math.PI, lat + (dLat * 180) / Math.PI];
}

/** Anneau fermé autour de `center`. */
export function circle(center: LonLat, radius: number, vertices = 64): LonLat[] {
  const ring = Array.from({ length: vertices }, (_, i) => offset(center, radius, (360 * i) / vertices));
  return [...ring, ring[0]!];
}

/** Profondeur du secteur sous le vent : base de l'agent (vent de 10 nœuds) × vent / 10, borné de 0,5 à 2. */
export function hazardDistance(downwind: number, windKnots: number): number {
  return downwind * Math.max(0.5, Math.min(2, windKnots / 10));
}

/** Secteur sous le vent : point de relâchement, arc de 30° centré sur la dérive, retour au point. */
export function hazardFan(release: LonLat, bearing: number, distance: number, arcs = 24): LonLat[] {
  const start = bearing - HAZARD_ARC_DEG / 2;
  const arc = Array.from({ length: arcs + 1 }, (_, i) => offset(release, distance, start + (HAZARD_ARC_DEG * i) / arcs));
  return [release, ...arc, release];
}

/** Distance en mètres (équirectangulaire, suffisante à l'échelle d'un camp). */
export function distanceM(a: LonLat, b: LonLat): number {
  const scale = Math.cos((((a[1] + b[1]) / 2) * Math.PI) / 180);
  return (Math.hypot((b[0] - a[0]) * scale, b[1] - a[1]) * Math.PI * EARTH_RADIUS_M) / 180;
}

/** Longueur d'une polyligne (m). */
export function lengthM(points: LonLat[]): number {
  return points.slice(1).reduce((sum, p, i) => sum + distanceM(points[i]!, p), 0);
}

function shift(coords: unknown, dLon: number, dLat: number): unknown {
  if (Array.isArray(coords) && typeof coords[0] === "number") {
    const [lon, lat, ...rest] = coords as number[];
    return [lon! + dLon, lat! + dLat, ...rest];
  }
  return Array.isArray(coords) ? coords.map((c) => shift(c, dLon, dLat)) : coords;
}

/** Aperçu d'un glissement : les entités du graphique `id` décalées de (dLon, dLat). */
export function translated(collection: FeatureCollection, id: string, dLon: number, dLat: number): FeatureCollection {
  return {
    ...collection,
    features: collection.features.map((f): Feature => {
      if (f.properties?.id !== id || !("coordinates" in f.geometry)) return f;
      const geometry = { ...f.geometry, coordinates: shift(f.geometry.coordinates, dLon, dLat) } as Geometry;
      return { ...f, geometry };
    }),
  };
}

/** Surface (km²) d'un anneau [lon, lat] : lacet plan corrigé de cos(lat), comme l'iPad. */
export function ringAreaKm2(ring: LonLat[]): number {
  if (ring.length < 3) return 0;
  const kLon = 111_320 * Math.cos((ring[0]![1] * Math.PI) / 180);
  const kLat = 111_132;
  let sum = 0;
  ring.forEach((a, i) => {
    const b = ring[(i + 1) % ring.length]!;
    sum += a[0] * kLon * (b[1] * kLat) - b[0] * kLon * (a[1] * kLat);
  });
  return Math.abs(sum) / 2 / 1_000_000;
}

/** Anneau auto-intersecté (« nœud papillon ») : surface et appartenance y seraient fausses. */
export function isSelfIntersecting(ring: LonLat[]): boolean {
  const n = ring.length;
  if (n < 4) return false;
  const orient = (p: LonLat, q: LonLat, r: LonLat) => (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]);
  for (let i = 0; i < n - 2; i++) {
    const a1 = ring[i]!;
    const a2 = ring[(i + 1) % n]!;
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue; // segments adjacents (fermeture)
      const b1 = ring[j]!;
      const b2 = ring[(j + 1) % n]!;
      const d1 = orient(a1, a2, b1);
      const d2 = orient(a1, a2, b2);
      const d3 = orient(b1, b2, a1);
      const d4 = orient(b1, b2, a2);
      if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) return true;
    }
  }
  return false;
}
